/*
  Ticket Import page. Composes the upload card, validation results,
  booking / release results and the seat-reassign modal — all the heavy
  lifting lives in `src/app/components/ticket-import/`.
*/
import React, { useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    message,
    Row,
    Space,
    Steps,
    Tag,
    Typography,
} from 'antd';
import { CheckCircleTwoTone, CloseCircleTwoTone } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { isValid, isValidArrayWithValues, isValidString } from '../../utils/utilities';
import { loadingAction } from '../../actions/loading-actions';
import { fetchSeatLayout } from '../../actions/home.action';
import { resetPassengerForm, validateTicketImport, bulkBookImportedTickets, bulkReleaseImportedTickets } from '../../actions/ticket.action';
import { UploadCard, ReassignSeatModal, renderValidationResults, renderBookingResults, renderReleaseResults, readWorkbookFromFile,
    parseExcelRows, buildHeaderMap, validateHeaders, extractTickets, groupIntoTickets, groupIntoTrips, findIgnoredTickets,
    findIneligibleTickets, buildValidationPayloadTrips, buildCancellationPayloadRows, buildBookingPayloadTrips } from '../../components/ticket-import/ticket-import.module';
const { Title, Text } = Typography;
const { Step } = Steps;

export const TicketImportPage = () => {
    const dispatch = useDispatch();

    // -- file state ------------------------------------------------------
    const [fileName, setFileName] = useState('');
    const [headerInfo, setHeaderInfo] = useState({ headers: [], missing: [] });
    const [parsedRows, setParsedRows] = useState([]);
    const [tickets, setTickets] = useState([]);
    const [trips, setTrips] = useState([]);
    const [hasParsed, setHasParsed] = useState(false);
    const [ignoredTickets, setIgnoredTickets] = useState([]);
    const [ineligibleTickets, setIneligibleTickets] = useState([]);

    // -- validation + selection state ------------------------------------
    const [validation, setValidation] = useState(null);
    const [isValidating, setIsValidating] = useState(false);
    const [selectedValidKeys, setSelectedValidKeys] = useState([]);
    const [selectedCancellationKeys, setSelectedCancellationKeys] = useState([]);

    // -- action result state ---------------------------------------------
    const [bookingResults, setBookingResults] = useState(null);
    const [isBooking, setIsBooking] = useState(false);
    const [releaseResults, setReleaseResults] = useState(null);
    const [isReleasing, setIsReleasing] = useState(false);

    // -- reassign-seat state ---------------------------------------------
    // seatOverrides keyed by `${tripKey}__${pnr}` → { [oldSeat]: newSeat }
    const [seatOverrides, setSeatOverrides] = useState({});
    const [reassignModal, setReassignModal] = useState({
        open: false,
        ticketKey: null,
        pnr: null,
        tripId: null,
        tripRouteId: null,
        oldSeatNumber: null,
        seats: [],
        loading: false,
    });

    const summary = useMemo(() => {
        const totalTickets = tickets.length;
        const totalTravellers = tickets.reduce((sum, ticket) => sum + ticket.travellers.length, 0);
        const activeTravellers = tickets.reduce(
            (sum, ticket) => sum + ticket.seats.filter((s) => s.isActive).length,
            0,
        );
        const totalFare = tickets.reduce((sum, ticket) => sum + ticket.totals.fare, 0);
        return {
            totalTickets,
            totalTravellers,
            activeTravellers,
            cancelledTravellers: totalTravellers - activeTravellers,
            totalTrips: trips.length,
            totalFare,
        };
    }, [tickets, trips]);

    const resetState = () => {
        setHeaderInfo({ headers: [], missing: [] });
        setParsedRows([]);
        setTickets([]);
        setTrips([]);
        setHasParsed(false);
        setValidation(null);
        setBookingResults(null);
        setSelectedValidKeys([]);
        setSelectedCancellationKeys([]);
        setReleaseResults(null);
        setIgnoredTickets([]);
        setIneligibleTickets([]);
        setSeatOverrides({});
        setReassignModal({
            open: false, ticketKey: null, pnr: null, tripId: null,
            tripRouteId: null, oldSeatNumber: null, seats: [], loading: false,
        });
    };

    // -- File handling ---------------------------------------------------
    const processFile = async (file) => {
        const extension = (file.name.split('.').pop() || '').toLowerCase();
        if (!['xlsx', 'xls', 'csv'].includes(extension)) {
            message.error('Please upload a valid Excel (.xlsx / .xls) or CSV file.');
            return;
        }
        setFileName(file.name);
        try {
            const { workbook, isCsv } = await readWorkbookFromFile(file);
            const allRows = parseExcelRows(workbook, isCsv);
            if (!isValidArrayWithValues(allRows)) {
                message.error('The file appears to be empty.');
                resetState();
                return;
            }
            const [headerRow, ...dataRows] = allRows;
            const headerMap = buildHeaderMap(headerRow);
            const missing = validateHeaders(headerMap);
            setHeaderInfo({
                headers: headerRow.filter((h) => isValidString(h)),
                missing,
            });
            setValidation(null);
            setBookingResults(null);
            if (missing.length > 0) {
                setParsedRows([]);
                setTickets([]);
                setTrips([]);
                setHasParsed(true);
                message.error(`Missing required column(s): ${missing.join(', ')}`);
                return;
            }
            const rows = extractTickets(dataRows, headerMap);
            if (!isValidArrayWithValues(rows)) {
                message.warning('No valid ticket rows found in the file.');
            }
            const ticketGroups = groupIntoTickets(rows);
            const tripGroups = groupIntoTrips(ticketGroups);
            const ignored = findIgnoredTickets(ticketGroups);
            const ineligible = findIneligibleTickets(ticketGroups);
            setParsedRows(rows);
            setTickets(ticketGroups);
            setTrips(tripGroups);
            setIgnoredTickets(ignored);
            setIneligibleTickets(ineligible);
            setHasParsed(true);
            const warningParts = [];
            if (ineligible.length > 0) {
                warningParts.push(`${ineligible.length} ticket(s) missing mandatory fields — not eligible to be booked`);
            }
            if (ignored.length > 0) {
                warningParts.push(`${ignored.length} ticket(s) with unrecognised status will be skipped`);
            }
            if (warningParts.length > 0) {
                message.warning(`Parsed ${rows.length} row(s) — ${warningParts.join('; ')}.`);
            } else {
                message.success(
                    `Parsed ${rows.length} row(s) into ${ticketGroups.length} ticket(s) across ${tripGroups.length} trip(s).`,
                );
            }
        } catch (error) {
            console.error('Failed to read ticket import file', error);
            message.error('Unable to read the uploaded file. Please verify the format and try again.');
            resetState();
        }
    };

    // -- Validate --------------------------------------------------------
    const runValidation = () => {
        const payloadTrips = buildValidationPayloadTrips(trips);
        const cancellationRows = buildCancellationPayloadRows(tickets);
        if (!isValidArrayWithValues(payloadTrips) && !isValidArrayWithValues(cancellationRows)) {
            if (ineligibleTickets.length > 0) {
                message.error(
                    `${ineligibleTickets.length} ticket(s) are not eligible to be booked due to missing mandatory fields. Fix the file and re-upload.`,
                );
            } else {
                message.warning('No active or cancellation rows found to validate.');
            }
            return;
        }
        if (!isValidString(global.operatorCode)) {
            message.error('Operator code is unavailable. Please re-login and try again.');
            return;
        }
        setIsValidating(true);
        setBookingResults(null);
        dispatch(loadingAction(true));
        validateTicketImport(
            payloadTrips,
            cancellationRows,
            (payload) => {
                setValidation(payload);
                const importableKeys = [];
                (payload.results || []).forEach((groupResult) => {
                    (groupResult.tickets || []).forEach((t) => {
                        if (t.canImport) {
                            importableKeys.push(`${groupResult.tripKey}__${t.pnr}`);
                        }
                    });
                });
                setSelectedValidKeys(importableKeys);
                const releasableKeys = [];
                (payload.cancellations || []).forEach((row) => {
                    if (row.canRelease) {
                        releasableKeys.push(`${row.tripKey}__${row.pnr}`);
                    }
                });
                setSelectedCancellationKeys(releasableKeys);
                message.success('Validation complete.');
                setIsValidating(false);
                dispatch(loadingAction(false));
            },
            () => {
                setIsValidating(false);
                dispatch(loadingAction(false));
            },
        );
    };

    // -- Bulk book -------------------------------------------------------
    const runBulkBooking = () => {
        if (!validation) {
            message.warning('Run validation first before attempting to book.');
            return;
        }
        if (!validation.summary || validation.summary.importableTickets === 0) {
            message.warning('No importable tickets — resolve the validation issues first.');
            return;
        }
        if (!isValidArrayWithValues(selectedValidKeys)) {
            message.warning('Select at least one ticket to book.');
            return;
        }
        const payloadTrips = buildBookingPayloadTrips(trips, selectedValidKeys, seatOverrides);
        if (!isValidArrayWithValues(payloadTrips)) {
            message.warning('No tickets selected for booking.');
            return;
        }
        if (!isValidString(global.operatorCode)) {
            message.error('Operator code is unavailable. Please re-login and try again.');
            return;
        }
        if (!isValid(global.agentId)) {
            message.error('Logged-in agent could not be determined. Please re-login and try again.');
            return;
        }
        setIsBooking(true);
        dispatch(loadingAction(true));
        bulkBookImportedTickets(
            payloadTrips,
            (payload) => {
                setBookingResults(payload);
                const booked = payload && payload.summary ? payload.summary.bookedTickets : 0;
                const failed = payload && payload.summary ? payload.summary.failedTickets : 0;
                if (failed === 0) {
                    message.success(`Bulk import complete. ${booked} ticket(s) booked.`);
                } else {
                    message.warning(`Bulk import done with issues — ${booked} booked, ${failed} failed.`);
                }
                setIsBooking(false);
                dispatch(loadingAction(false));
            },
            () => {
                setIsBooking(false);
                dispatch(loadingAction(false));
            },
        );
    };

    // -- Bulk release ----------------------------------------------------
    const runBulkRelease = () => {
        if (!validation) {
            message.warning('Run validation first before attempting to release.');
            return;
        }
        if (!isValidArrayWithValues(selectedCancellationKeys)) {
            message.warning('Select at least one cancellation row to release.');
            return;
        }
        const validCancellations = isValidArrayWithValues(validation.cancellations)
            ? validation.cancellations
            : [];
        const selectedSet = new Set(selectedCancellationKeys);
        const payloadCancellations = validCancellations
            .filter((row) => (
                selectedSet.has(`${row.tripKey}__${row.pnr}`) && row.canRelease
            ))
            .map((row) => ({
                tripKey: row.tripKey,
                journeyDate: row.journeyDate,
                route: row.route,
                serviceNumber: row.serviceNumber,
                pnr: row.pnr,
                boardingPoint: row.boardingPoint,
                dropOffPoint: row.dropOffPoint,
            }));
        if (!isValidArrayWithValues(payloadCancellations)) {
            message.warning('No matching cancellation rows can be released.');
            return;
        }
        if (!isValidString(global.operatorCode)) {
            message.error('Operator code is unavailable. Please re-login and try again.');
            return;
        }
        setIsReleasing(true);
        dispatch(loadingAction(true));
        bulkReleaseImportedTickets(
            payloadCancellations,
            (payload) => {
                setReleaseResults(payload);
                const summary = payload && payload.summary;
                const released = summary ? summary.releasedTickets : 0;
                const failed = summary ? summary.failedCancellations : 0;
                if (failed === 0) {
                    message.success(`Released ${released} ticket(s).`);
                } else {
                    message.warning(`Release done with issues — ${released} released, ${failed} failed.`);
                }
                setIsReleasing(false);
                dispatch(loadingAction(false));
            },
            () => {
                setIsReleasing(false);
                dispatch(loadingAction(false));
            },
        );
    };

    // -- Seat reassignment ----------------------------------------------
    const openReassignModal = (ticketKey, pnr, tripId, tripRouteId, oldSeatNumber) => {
        if (!isValid(tripRouteId)) {
            message.error('Unable to open seat picker — trip route id is missing for this ticket.');
            return;
        }
        // SeatLayoutContainer dispatches `updateSelectedSeats` to the shared
        // passenger-form redux slice on every click. Reset it before showing
        // the modal so previous selections don't appear pre-selected here.
        dispatch(resetPassengerForm());
        setReassignModal({
            open: true,
            ticketKey,
            pnr,
            tripId,
            tripRouteId,
            oldSeatNumber,
            seats: [],
            loading: true,
        });
        dispatch(fetchSeatLayout(
            tripRouteId,
            (payload) => {
                setReassignModal((current) => ({
                    ...current,
                    seats: (payload && payload.seats) || [],
                    loading: false,
                }));
            },
            (err) => {
                message.error(err || 'Failed to load seat layout.');
                setReassignModal((current) => ({ ...current, open: false, loading: false }));
            },
        ));
    };

    const closeReassignModal = () => {
        setReassignModal({
            open: false, ticketKey: null, pnr: null, tripId: null,
            tripRouteId: null, oldSeatNumber: null, seats: [], loading: false,
        });
        dispatch(resetPassengerForm());
    };

    const applySeatOverride = (newSeatNumber) => {
        const { ticketKey, oldSeatNumber } = reassignModal;
        if (!ticketKey || !oldSeatNumber) {
            closeReassignModal();
            return;
        }
        setSeatOverrides((prev) => ({
            ...prev,
            [ticketKey]: {
                ...(prev[ticketKey] || {}),
                [oldSeatNumber]: newSeatNumber,
            },
        }));
        setSelectedValidKeys((current) => (
            current.indexOf(ticketKey) === -1 ? [...current, ticketKey] : current
        ));
        message.success(`Seat ${oldSeatNumber} will be replaced with ${newSeatNumber} for this PNR.`);
        closeReassignModal();
    };

    const clearSeatOverride = (ticketKey, oldSeatNumber) => {
        setSeatOverrides((prev) => {
            const ticketOverrides = { ...(prev[ticketKey] || {}) };
            delete ticketOverrides[oldSeatNumber];
            const next = { ...prev };
            if (Object.keys(ticketOverrides).length === 0) {
                delete next[ticketKey];
            } else {
                next[ticketKey] = ticketOverrides;
            }
            return next;
        });
    };

    // -- Workflow step indicator -----------------------------------------
    const currentStep = (() => {
        if (!hasParsed) return 0;
        if (!validation) return 1;
        if (bookingResults || releaseResults) return 3;
        return 2;
    })();
    const importableSelected = selectedValidKeys.length;
    const releasableSelected = selectedCancellationKeys.length;
    // Pre-validation: count cancellation rows in the file so we can pre-tell
    // the operator how many releases will be looked up.
    const cancellationRowCount = useMemo(() => {
        return buildCancellationPayloadRows(tickets).length;
    }, [tickets]);
    // Make the Release action visually primary when the only thing this file
    // does is release previously-blocked tickets — otherwise the Book button
    // is the primary call-to-action.
    const releaseIsPrimaryAction = validation
        && validation.summary
        && (validation.summary.importableTickets || 0) === 0
        && (validation.summary.releasableCancellations || 0) > 0;

    return (
        <div className="ticket-import-root" style={{ padding: 16, maxWidth: 1280, margin: '0 auto' }}>
            {/* Header + workflow steps */}
            <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col>
                    <Title level={3} style={{ margin: 0 }}>Ticket Import</Title>
                    <Text type="secondary">
                        Upload a Data Backup Export Report, validate against existing trips, then
                        bulk import.
                    </Text>
                </Col>
                <Col flex="auto" style={{ maxWidth: 520 }}>
                    <Steps size="small" current={currentStep}>
                        <Step title="Upload" />
                        <Step title="Validate" />
                        <Step title="Import" />
                    </Steps>
                </Col>
            </Row>

            <UploadCard
                hasParsed={hasParsed}
                fileName={fileName}
                parsedRowsCount={parsedRows.length}
                ticketsCount={summary.totalTickets}
                tripsCount={summary.totalTrips}
                processFile={processFile}
                onClear={() => { setFileName(''); resetState(); }}
            />

            {/* Action bar */}
            {hasParsed && headerInfo.missing.length === 0 && (
                <Card style={{ marginTop: 16 }}>
                    <Row justify="space-between" align="middle" gutter={[16, 8]}>
                        <Col>
                            {!validation && (
                                <Space direction="vertical" size={2}>
                                    <Text>
                                        File parsed. <Text strong>{summary.totalTickets}</Text> ticket(s)
                                        detected — validate them against existing trips to continue.
                                    </Text>
                                    {cancellationRowCount > 0 && (
                                        <Text type="secondary">
                                            <Tag color="orange" style={{ marginRight: 4 }}>↩</Tag>
                                            <Text strong>{cancellationRowCount}</Text>
                                            {' cancellation row(s) detected — validation will look them up so you can release the previously-imported tickets.'}
                                        </Text>
                                    )}
                                </Space>
                            )}
                            {validation && validation.summary && (
                                <Space size={16} wrap>
                                    <span>
                                        <CheckCircleTwoTone twoToneColor="#52c41a" />
                                        {' '}
                                        <Text strong>{validation.summary.importableTickets}</Text>
                                        {' '}
                                        <Text type="secondary">importable</Text>
                                    </span>
                                    <span>
                                        <CloseCircleTwoTone twoToneColor="#ff4d4f" />
                                        {' '}
                                        <Text strong>
                                            {validation.summary.totalTickets - validation.summary.importableTickets}
                                        </Text>
                                        {' '}
                                        <Text type="secondary">cannot import</Text>
                                    </span>
                                    {validation.summary.totalCancellations > 0 && (
                                        <span>
                                            <Tag color="orange">↩</Tag>
                                            <Text strong>{validation.summary.releasableCancellations || 0}</Text>
                                            {' '}
                                            <Text type="secondary">cancellations to release</Text>
                                        </span>
                                    )}
                                </Space>
                            )}
                        </Col>
                        <Col>
                            <Space>
                                {!validation && tickets.length > 0 && (
                                    <Button
                                        type="primary"
                                        loading={isValidating}
                                        onClick={runValidation}
                                    >
                                        Validate against existing trips
                                    </Button>
                                )}
                                {validation && validation.summary && validation.summary.importableTickets > 0 && (
                                    <Button
                                        type="primary"
                                        loading={isBooking}
                                        disabled={importableSelected === 0}
                                        onClick={runBulkBooking}
                                    >
                                        {importableSelected > 0
                                            ? `Book selected (${importableSelected})`
                                            : 'Select ticket(s) to book'}
                                    </Button>
                                )}
                                {validation && validation.summary && validation.summary.releasableCancellations > 0 && (
                                    <Button
                                        type={releaseIsPrimaryAction ? 'primary' : 'default'}
                                        danger={releaseIsPrimaryAction}
                                        loading={isReleasing}
                                        disabled={releasableSelected === 0}
                                        onClick={runBulkRelease}
                                    >
                                        {releasableSelected > 0
                                            ? `Release ${releasableSelected} cancelled ticket(s)`
                                            : 'Select cancellations to release'}
                                    </Button>
                                )}
                                {validation && (
                                    <Button
                                        loading={isValidating}
                                        onClick={runValidation}
                                    >
                                        Re-validate
                                    </Button>
                                )}
                            </Space>
                        </Col>
                    </Row>
                </Card>
            )}

            {/* Header missing error */}
            {hasParsed && headerInfo.missing.length > 0 && (
                <Alert
                    style={{ marginTop: 16 }}
                    type="error"
                    showIcon
                    message={`Missing required column(s): ${headerInfo.missing.join(', ')}`}
                    description="Fix the header row in your file and re-upload."
                />
            )}

            {/* Validation panel */}
            {hasParsed && headerInfo.missing.length === 0 && (
                <div style={{ marginTop: 16 }}>
                    {renderValidationResults(
                        validation,
                        tickets,
                        selectedValidKeys,
                        setSelectedValidKeys,
                        selectedCancellationKeys,
                        setSelectedCancellationKeys,
                        ignoredTickets,
                        ineligibleTickets,
                        seatOverrides,
                        openReassignModal,
                        clearSeatOverride,
                    )}
                </div>
            )}

            {/* Inline results */}
            {bookingResults && (
                <Card
                    style={{ marginTop: 16 }}
                    title={(
                        <Space>
                            <CheckCircleTwoTone twoToneColor="#52c41a" />
                            <Text strong>Bulk booking results</Text>
                        </Space>
                    )}
                >
                    {renderBookingResults(bookingResults)}
                </Card>
            )}

            {releaseResults && (
                <Card
                    style={{ marginTop: 16 }}
                    title={(
                        <Space>
                            <Tag color="orange">↩</Tag>
                            <Text strong>Cancellation release results</Text>
                        </Space>
                    )}
                >
                    {renderReleaseResults(releaseResults)}
                </Card>
            )}

            <ReassignSeatModal
                state={reassignModal}
                onClose={closeReassignModal}
                onPickSeat={applySeatOverride}
            />
        </div>
    );
};
